import type { EntityId } from './ids.js';
import type { PrivacyClass } from './privacy.js';
import type { RecordEnvelope } from './records.js';

/**
 * Target — the canonical, reusable "tracker" abstraction. Goals → milestones →
 * TARGETS → systems/routines/habits/tasks → evidence → streak → review.
 * A target is NOT a habit and NOT a task; it is a measured commitment that can
 * generate or link to any of them. Misses create review, never shame.
 */
export type TargetType =
  | 'outcome_target'
  | 'output_target'
  | 'practice_target'
  | 'learning_target'
  | 'maintenance_target'
  | 'repair_target'
  | 'growth_target';

export const TARGET_TYPES: readonly TargetType[] = [
  'outcome_target',
  'output_target',
  'practice_target',
  'learning_target',
  'maintenance_target',
  'repair_target',
  'growth_target',
] as const;

export type TargetCadence = 'daily' | 'weekly' | 'monthly';
export const TARGET_CADENCES: readonly TargetCadence[] = ['daily', 'weekly', 'monthly'] as const;

export type TargetStatus = 'active' | 'at_risk' | 'paused' | 'met' | 'retired';

export interface Target extends RecordEnvelope {
  readonly kind: 'target';
  readonly title: string;
  readonly targetType: TargetType;
  readonly cadence: TargetCadence;
  /** How many evidence entries per cadence window prove the target was met. */
  readonly quantity: number;
  /** The standard each evidence entry should meet (e.g. "meaningful commit"). */
  readonly qualityBar: string;
  /** What counts as evidence (the anti-fake-activity rule). */
  readonly evidenceRule: string;
  /** The smallest action that still counts on a hard day (recovery policy). */
  readonly minimumViableAction: string;
  readonly status: TargetStatus;
  /** Linked goals/projects/skills this target advances (ids only, no duplication). */
  readonly linkedIds: readonly EntityId[];
}

export interface TargetInput {
  readonly title: string;
  readonly targetType?: TargetType;
  readonly cadence?: TargetCadence;
  readonly quantity?: number;
  readonly qualityBar?: string;
  readonly evidenceRule?: string;
  readonly minimumViableAction?: string;
  readonly privacyClass?: PrivacyClass;
}

/** Consistency evidence — proof a target was practiced. Never a standalone score. */
export interface ConsistencyEvidence extends RecordEnvelope {
  readonly kind: 'consistency_evidence';
  readonly targetId: EntityId;
  /** ISO date the evidence is counted under. */
  readonly occurredOn: string;
  readonly note: string;
}

export interface ConsistencySummary {
  readonly targetId: EntityId;
  readonly cadence: TargetCadence;
  readonly quantity: number;
  /** Evidence count in the current cadence window. */
  readonly currentWindowCount: number;
  /** Whether the current window already meets the quantity. */
  readonly currentWindowMet: boolean;
  /** Consecutive prior windows (not counting current) that met quantity. */
  readonly streakWindows: number;
  readonly lastEvidenceAt: string | null;
  /** Neutral, non-shaming status word. */
  readonly state: 'on_track' | 'in_progress' | 'needs_attention' | 'paused';
}

/** Window key for a date under a cadence (UTC-based, deterministic). */
export function windowKey(cadence: TargetCadence, iso: string): string {
  const d = new Date(iso);
  const y = d.getUTCFullYear();
  if (cadence === 'daily') return iso.slice(0, 10);
  if (cadence === 'monthly') return `${y}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  // weekly: ISO week number
  const tmp = new Date(Date.UTC(y, d.getUTCMonth(), d.getUTCDate()));
  const day = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${tmp.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

function previousWindowKeyIso(cadence: TargetCadence, iso: string): string {
  const d = new Date(iso);
  if (cadence === 'daily') d.setUTCDate(d.getUTCDate() - 1);
  else if (cadence === 'weekly') d.setUTCDate(d.getUTCDate() - 7);
  else d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString();
}

/**
 * Pure consistency computation. Streak counts consecutive *prior* windows that
 * met the quantity; the current window is reported separately so a not-yet-done
 * day never reads as a broken streak.
 */
export function computeConsistency(
  target: Pick<Target, 'id' | 'cadence' | 'quantity' | 'status'>,
  evidence: readonly ConsistencyEvidence[],
  nowIso: string,
): ConsistencySummary {
  const counts = new Map<string, number>();
  let lastEvidenceAt: string | null = null;
  for (const e of evidence) {
    const k = windowKey(target.cadence, e.occurredOn);
    counts.set(k, (counts.get(k) ?? 0) + 1);
    if (!lastEvidenceAt || e.occurredOn > lastEvidenceAt) lastEvidenceAt = e.occurredOn;
  }

  const currentKey = windowKey(target.cadence, nowIso);
  const currentWindowCount = counts.get(currentKey) ?? 0;
  const currentWindowMet = currentWindowCount >= target.quantity;

  let streakWindows = 0;
  let cursorIso = previousWindowKeyIso(target.cadence, nowIso);
  // Walk backwards through prior windows while each met the quantity.
  for (let i = 0; i < 520; i++) {
    const k = windowKey(target.cadence, cursorIso);
    if ((counts.get(k) ?? 0) >= target.quantity) {
      streakWindows += 1;
      cursorIso = previousWindowKeyIso(target.cadence, cursorIso);
    } else {
      break;
    }
  }

  let state: ConsistencySummary['state'];
  if (target.status === 'paused') state = 'paused';
  else if (currentWindowMet) state = 'on_track';
  else if (currentWindowCount > 0) state = 'in_progress';
  else state = streakWindows > 0 ? 'in_progress' : 'needs_attention';

  return {
    targetId: target.id,
    cadence: target.cadence,
    quantity: target.quantity,
    currentWindowCount,
    currentWindowMet,
    streakWindows,
    lastEvidenceAt,
    state,
  };
}

